import React, { forwardRef, useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';

import useUserContext from '../hooks/use-user-context';
import useOsanoContext from '../hooks/use-osano-context';
import { isVisitorBot, mergeQueryParams, setOsanoParam } from '../utilities/misc';
// Link component that delays click events until after the component has rehydrated.
// Uses window.postHydrationTrigger function found in gatsby-ssr.js
const LinkQueryParams = forwardRef(({
  children,
  className,
  href,
  itemProp,
  itemScope,
  itemType,
  onClick,
  onKeyDown,
  rel,
  role,
  tabIndex,
  target,
  'aria-current': ariaCurrent,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy
}, ref) => {
  const { hasCookieLoaded, cookieData } = useUserContext();

  const uniqueId = useId();
  const [hrefWithQueryParams, setHrefWithQueryParams] = useState(null);
  const osanoContext = useOsanoContext();

  useEffect(() => {
    if (hasCookieLoaded) {
      const currentCookie = { ...cookieData };
      const disabledParams = {
        web_source: 'disabled',
        web_medium: 'disabled',
        page: 'disabled'
      };
      // eslint-disable-next-line no-restricted-globals
      const currentContext = { ...osanoContext };
      const paramsToToggle = ['page', 'web_source', 'web_medium'];
      paramsToToggle.forEach((par) => {
        // eslint-disable-next-line no-prototype-builtins
        if (osanoContext.beenDisabled === true && Object.keys(currentCookie).includes(par)) {
          currentCookie[par] = setOsanoParam(currentCookie, par, currentContext);
        }
      });
      // debug('LinkQueryParams - currentCookie:', currentCookie);
      const mergeableCookie = osanoContext.ANALYTICS === 'DENY' ? disabledParams : currentCookie;
      const newHref = !isVisitorBot() ? mergeQueryParams(href, mergeableCookie) : href;
      // debug('LinkQueryParams - newHref', newHref);
      setHrefWithQueryParams(newHref);
    }
  }, [hasCookieLoaded, cookieData, osanoContext, href]);

  useEffect(() => {
    if (hrefWithQueryParams !== null && typeof window.postHydrationTrigger === 'function') {
      window.postHydrationTrigger(uniqueId);
    }
  }, [hrefWithQueryParams, uniqueId]);

  return (
    <a
      ref={ref}
      href={hrefWithQueryParams || href}
      target={target}
      className={className}
      rel={rel}
      role={role}
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown}
      itemProp={itemProp}
      itemScope={itemScope}
      itemType={itemType}
      aria-current={ariaCurrent}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      data-link-id={uniqueId}
    >
      {children}
    </a>
  );
});

LinkQueryParams.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  href: PropTypes.string,
  itemProp: PropTypes.string,
  itemScope: PropTypes.bool,
  itemType: PropTypes.string,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  rel: PropTypes.string,
  role: PropTypes.string,
  tabIndex: PropTypes.number,
  'aria-current': PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string
};

export default LinkQueryParams;
